/**
 * @fileoverview Presentational layout for the Chat page.
 * @module pages/chat/ChatView
 */
import styles from "../../styles/Chat.module.css";
import LanguageSelector from "../../components/LanguageSelector/index.jsx";
import NotificationBell from "../../components/NotificationBell/index.jsx";
import SearchBar from "../../components/SearchBar/index.jsx";
import {
  InboxView,
  ConnectionRequestsView,
  ChatContainer,
} from "../../components/Chat";
import { CircularProgress, Backdrop } from "@mui/material";
import { ReactComponent as TitleIcon } from "../../assets/icons/Chat/TitleIcon.svg";

/** Renders the Chat page layout from a pre-built view-model. */
const ChatView = ({
  allowed,
  t,
  navigate,
  searchBarWidth,
  header,
  inbox,
  connectionRequests,
  chatContainer,
  overlay,
}) => {
  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon />
            <h1 className={styles.title}>{t("pages.chat.header")}</h1>
          </div>
          <div className={styles.headerControls}>
            <SearchBar
              placeholder={t("pages.chat.searchText")}
              value={header.searchQuery}
              onChange={header.onSearch}
              className={styles.searchSection}
              width={searchBarWidth}
            />
          </div>
        </div>
      </div>

      <div className={styles.mainContainer}>
        <div className={styles.mainContent}>
          {inbox.activeView === "inbox" ? (
            <InboxView
              chats={inbox.chats}
              searchQuery={inbox.searchQuery}
              users={inbox.users}
              companies={inbox.companies}
              selectedChat={inbox.selectedChat}
              activeConnections={inbox.activeConnections}
              onChatSelect={inbox.onChatSelect}
              onViewChange={inbox.onViewChange}
              activeView={inbox.activeView}
              connectionRequests={inbox.connectionRequests}
              className={`${inbox.hideInbox ? styles.hideInbox : ""}`}
            />
          ) : (
            <ConnectionRequestsView
              connectionRequests={connectionRequests.connectionRequests}
              onViewChange={connectionRequests.onViewChange}
              activeView={connectionRequests.activeView}
              chats={connectionRequests.chats}
              onChatsUpdate={connectionRequests.onChatsUpdate}
              onChatParticipantsUpdate={
                connectionRequests.onChatParticipantsUpdate
              }
              onConnectionRequestsUpdate={
                connectionRequests.onConnectionRequestsUpdate
              }
              navigate={connectionRequests.navigate}
              onSimulatedChatOpen={connectionRequests.onSimulatedChatOpen}
            />
          )}

          <ChatContainer
            selectedChat={chatContainer.selectedChat}
            selectedUser={chatContainer.selectedUser}
            selectedCompany={chatContainer.selectedCompany}
            chatMessages={chatContainer.chatMessages}
            messageInput={chatContainer.messageInput}
            setMessageInput={chatContainer.setMessageInput}
            messageEndRef={chatContainer.messageEndRef}
            activeConnections={chatContainer.activeConnections}
            isReconnecting={chatContainer.isReconnecting}
            isUserActive={chatContainer.isUserActive}
            showFileUpload={chatContainer.showFileUpload}
            selectedFile={chatContainer.selectedFile}
            isFileUploading={chatContainer.isFileUploading}
            isDragging={chatContainer.isDragging}
            fileError={chatContainer.fileError}
            users={chatContainer.users}
            setChats={chatContainer.setChats}
            setSelectedChat={chatContainer.setSelectedChat}
            setSelectedUser={chatContainer.setSelectedUser}
            onSendMessage={chatContainer.onSendMessage}
            onKeyPress={chatContainer.onKeyPress}
            onAttachFilesClick={chatContainer.onAttachFilesClick}
            onFileUploadClose={chatContainer.onFileUploadClose}
            onFileClick={chatContainer.onFileClick}
            onFileChange={chatContainer.onFileChange}
            onDragOver={chatContainer.onDragOver}
            onDragLeave={chatContainer.onDragLeave}
            onDrop={chatContainer.onDrop}
            onBackToInbox={chatContainer.onBackToInbox}
            className={`${chatContainer.showChat ? styles.showChat : ""}`}
            readOnly={chatContainer.readOnly}
            headerTitleOverride={chatContainer.headerTitleOverride}
            readOnlyRequestType={chatContainer.readOnlyRequestType}
            readOnlyRequest={chatContainer.readOnlyRequest}
            onConnectionRequestsUpdate={
              chatContainer.onConnectionRequestsUpdate
            }
            onChatParticipantsUpdate={chatContainer.onChatParticipantsUpdate}
            onClearReadOnlyChat={chatContainer.onClearReadOnlyChat}
          />
        </div>
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={overlay.loading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{ color: "#4eabe7" }}
        />
      </Backdrop>
    </div>
  );
};

export default ChatView;
